import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import BotQuickLink from "../components/BotQuickLink";
import PublicHeader from "../components/PublicHeader";
import { Building2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const officeSlug = searchParams.get("office") || "";

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [offices, setOffices] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(officeSlug);

  // Load public offices to give context + dropdown if no slug in URL
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/offices/public");
        setOffices(data || []);
        // Auto-pick first office if there's only one and no slug given
        if (!officeSlug && data && data.length === 1) {
          setSelectedSlug(data[0].slug);
        }
      } catch (e) {
        // Public office list is best-effort: if endpoint fails, we still render
        // the form (backend will fall back to the first public office on register).
      }
    })();
  }, [officeSlug]);

  const currentOffice = offices.find((o) => o.slug === selectedSlug);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, selectedSlug || undefined);
      // Try to generate a link code and redirect DIRECTLY to the bot deep link
      try {
        const { data } = await api.post("/telegram/link-code");
        if (data?.deep_link) {
          toast.success("Account creato! Ti porto al bot Telegram…");
          window.location.href = data.deep_link;
          return;
        }
      } catch (e) {
        // Deep-link generation is best-effort (bot may be offline for this office).
        // Fall through to the normal /menu redirect below — user can link Telegram later.
        console.warn("Telegram deep-link unavailable, skipping auto-redirect:", e?.message);
      }
      toast.success("Account creato!");
      navigate("/menu", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grain flex flex-col">
      <PublicHeader ctaTo="/login" ctaLabel="Accedi" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">🍱</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            Crea il tuo account
          </h1>
          {currentOffice && (
            <div
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
              data-testid="register-office-badge"
            >
              <Building2 className="w-3 h-3" />
              {currentOffice.name}
            </div>
          )}
        </div>
        <div className="bg-card border border-border rounded-lg p-7 sm:p-8">
          <form onSubmit={onSubmit} className="space-y-4">
            {offices.length > 1 && (
              <div>
                <label className="block text-sm font-medium mb-1.5">Ufficio</label>
                <select
                  required
                  value={selectedSlug}
                  onChange={(e) => setSelectedSlug(e.target.value)}
                  data-testid="register-office-select"
                  className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                >
                  <option value="">— Seleziona il tuo ufficio —</option>
                  {offices.map((o) => (
                    <option key={o.slug} value={o.slug}>
                      {o.name}
                      {o.location ? ` (${o.location})` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Il tuo ufficio non è in lista?{" "}
                  <Link to="/contatti" className="text-primary hover:underline" data-testid="register-link-contact">
                    Richiedi attivazione
                  </Link>
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1.5">Nome</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name-input"
                className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="register-email-input"
                className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password-input"
                className="w-full px-3 py-2.5 bg-white border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">Almeno 6 caratteri</p>
            </div>
            {error && (
              <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md p-3" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full py-2.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] disabled:opacity-50 transition-all duration-200"
            >
              {loading ? "Creazione…" : "Crea account"}
            </button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Hai già un account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
              Accedi
            </Link>
          </p>
        </div>

        <div className="mt-5">
          <BotQuickLink variant="compact" />
        </div>
      </div>
      </div>
    </div>
  );
}
